import { AsyncPipe, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { heroArrowDownTray } from '@ng-icons/heroicons/outline';
import { IconComponent } from 'app/shared/icon/icon.component';
import { ImagePreviewComponent } from '../image-preview/image-preview.component';
import { ValueAccessorDirective } from '../value-accessor.directive';
import { IPreviewUrls } from '../file.model';
import { NotificationService } from '../../notification.service';
import { tap } from 'rxjs';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [IconComponent, AsyncPipe, ImagePreviewComponent, NgIf],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  host: {
    class: 'h-[166px] flex flex-col items-start justify-center',
  },
  providers: [
    provideIcons({
      heroArrowDownTray,
    }),
  ],
  hostDirectives: [ValueAccessorDirective],
})
export class FileUploadComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private http = inject(HttpClient);
  private cd = inject(ChangeDetectorRef);
  private valueAccessor = inject(ValueAccessorDirective);
  private notification = inject(NotificationService);

  apiUrl = input<string>();
  dragDrop = input<boolean>();
  requiredFileType = input('image/*');
  showPreview = input(true);
  mode = input<'single' | 'multiple'>('single');
  isMultiple = computed(() => this.mode() === 'multiple');
  fileSelected = output<File>();
  previewUrl = signal<null | string>(null);
  previewUrls = output<IPreviewUrls[]>();
  previewUrl$ = toObservable(this.previewUrl);
  isDragHover = signal(false);
  selectedFiles = signal<File[]>([]);
  maxSize = 20971520; // equal to 20Mb

  ngOnInit(): void {
    this.valueAccessor.value.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) {
        this.previewUrl.set(null);
      } else if (typeof value === 'string') {
        this.previewUrl.set(value);
      } else if (value instanceof File) {
        this.previewUrl.set(URL.createObjectURL(value));
      } else if (Array.isArray(value)) {
        this.selectedFiles.set(value);
        this.previewUrlsChange();
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files) return;

    const filteredFiles = [...input.files].filter((file) => {
      return file.size <= this.maxSize && file.type.startsWith('image');
    });

    if (filteredFiles.length !== input.files.length) {
      this.notification.error('Part of files not added due to invalid type or size amount');
    }

    this.fileSelectedHandler(filteredFiles);
    input.value = '';
  }

  removeFile() {
    this.valueAccessor.valueChange(null);
    this.previewUrl.set(null);
  }

  fileSelectedHandler(files: File[] | null) {
    if (!files) return;
    if (this.isMultiple()) {
      this.selectedFiles.update((selectedFiles) => files.concat(selectedFiles));
      this.valueAccessor.valueChange(this.selectedFiles());
      this.previewUrlsChange();
    } else {
      const file = files[0];
      if (!file) return;
      this.valueAccessor.valueChange(file);
      this.previewUrl.set(URL.createObjectURL(file));
      this.fileSelected.emit(file);
    }
    this.valueAccessor.touchedChange(true);
  }

  private previewUrlsChange() {
    const isIPreviewUrls = (value: any): value is IPreviewUrls =>
      value && typeof value.name === 'string' && typeof value.url === 'string';

    const urls = this.selectedFiles()
      .map((value) => {
        if (value instanceof File) {
          return {
            name: value.name,
            url: URL.createObjectURL(value),
          };
        }

        if (isIPreviewUrls(value)) {
          return value;
        }

        return null;
      })
      .filter((value) => value !== null);

    this.previewUrls.emit(urls);
  }

  uploadFile(file: File) {
    if (!this.apiUrl()) return;
    const formData = new FormData();

    formData.append('file', file);
    formData.append('name', file.name);

    this.http
      .post(this.apiUrl() as string, formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => console.log('File uploaded successfully', response),
        error: (err) => console.error('File upload failed', err),
      });
  }

  dropHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
    this.dragLeaveHandler();
    if (ev.dataTransfer?.items) {
      // Use DataTransferItemList interface to access the file(s)
      const files = [...ev.dataTransfer.items]
        .map((item) => {
          // If dropped items aren't files, reject them
          if (item.kind !== 'file') {
            return;
          }
          const file = item.getAsFile();
          return file;
        })
        .filter((f) => !!f);
      this.fileSelectedHandler(files);
    } else if (ev.dataTransfer?.files) {
      this.fileSelectedHandler([...ev.dataTransfer.files]);
    }
  }

  private arrayToFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    return dataTransfer.files;
  }

  dragOverHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
    this.isDragHover.set(true);
  }

  dragLeaveHandler() {
    this.isDragHover.set(false);
  }
}
